import type { ReactNode } from 'react';
import {
  Sheet,
  SheetClose,
  SheetContentResizable,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useLocalStorageValue } from '@react-hookz/web';
import { T, useGT } from 'gt-react';
import { SparkleIcon, XIcon } from '@phosphor-icons/react';
import { useNamiSheetDock } from '@/components/nami/useNamiSheetDock';
import {
  EntityDetailTabsProvider,
  EntityDetailTabsHeader,
  EntityDetailTabsContent,
  useEntityDetailTabsContext,
} from '@/components/system-entities/entity-detail-tabs';
import { SystemEntity } from '@erp/shared-types';
import { Tabs } from '@/components/ui/tabs';
import { InvoiceActionsMenu } from '@/components/accounts-payable/invoice-detail/InvoiceActionsMenu';
import {
  InvoiceSlideinActionsProvider,
  InvoiceSlideinActionsSlot,
} from '@/components/accounts-payable/invoice-detail/invoice-slidein-actions';

/**
 * The invoice detail body: related-entity tabs, exceptions, and actions.
 * The slide-in and a task-page AP invoice placement both mount this, so a
 * task anchored to an invoice shows the same panel as the invoice page.
 */
export function InvoiceDetailPanel({
  invoiceId,
  headerLeading,
  headerTrailing,
}: {
  invoiceId: string;
  /** Record navigation for the invoices list. Omitted when the panel is embedded. */
  headerLeading?: ReactNode;
  /** Close control for the slide-in. Omitted when the panel is embedded. */
  headerTrailing?: ReactNode;
}) {
  return (
    <EntityDetailTabsProvider
      key={invoiceId}
      initialEntity={{
        entityType: SystemEntity.AP_INVOICE,
        entityId: invoiceId,
      }}
    >
      <InvoiceDetailTabs
        invoiceId={invoiceId}
        headerLeading={headerLeading}
        headerTrailing={headerTrailing}
      />
    </EntityDetailTabsProvider>
  );
}

function InvoiceDetailTabs({
  invoiceId,
  headerLeading,
  headerTrailing,
}: {
  invoiceId: string;
  headerLeading?: ReactNode;
  headerTrailing?: ReactNode;
}) {
  const { activeTabId, setActiveTabId } = useEntityDetailTabsContext();
  return (
    <Tabs
      value={activeTabId}
      onValueChange={setActiveTabId}
      className="flex min-h-0 flex-1 flex-col"
    >
      <SheetHeader className="shrink-0 grow-0 flex-row items-end gap-0">
        <div className="min-w-0 flex-1">
          <EntityDetailTabsHeader />
        </div>
        {/* The tab list owns the underline. This strip continues that same
            line under the actions so the header stays one piece. */}
        <div className="flex h-9 shrink-0 items-center gap-1 border-b pl-3">
          {headerLeading}
          <InvoiceActionsMenu key={invoiceId} invoiceId={invoiceId} />
          {headerTrailing}
        </div>
      </SheetHeader>
      <div className="min-h-0 flex-1 overflow-hidden">
        <EntityDetailTabsContent />
      </div>
    </Tabs>
  );
}

/** Opens (or closes) the docked assistant beside the slide-in, which hides the pill. */
function AskAiButton({
  pressed,
  onToggle,
}: {
  pressed: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      variant={pressed ? 'secondary' : 'ghost'}
      size="sm"
      aria-pressed={pressed}
      onClick={onToggle}
    >
      <SparkleIcon
        weight="fill"
        aria-hidden
        className="size-4 text-purple-600 dark:text-purple-400"
      />
      <T context="invoice slide-in header button that opens the AI assistant panel beside the invoice">
        Ask AI
      </T>
    </Button>
  );
}

export function InvoiceDetailSlidein({
  isOpen,
  onOpenChange,
  invoiceId,
  headerLeading,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId?: string;
  /** Previous/next for the invoices list. Only the invoices route supplies it. */
  headerLeading?: ReactNode;
}) {
  const gt = useGT();
  const { value: detailViewSize, set: setDetailViewSize } =
    useLocalStorageValue<number>('invoice-detail-view-size', {
      defaultValue: 50,
    });
  const nami = useNamiSheetDock(onOpenChange);

  return (
    <Sheet open={isOpen} {...nami.sheetProps}>
      <SheetContentResizable
        hideCloseButton
        className={nami.contentClassName}
        overlayClassName={nami.overlayClassName}
        resizableProps={{
          defaultSize: detailViewSize,
          onResize: setDetailViewSize,
          className: 'flex flex-col',
        }}
      >
        <InvoiceSlideinActionsProvider>
          {invoiceId ? (
            <InvoiceDetailPanel
              invoiceId={invoiceId}
              // Ask AI leads the header strip, before the list navigation and the ⋯ menu.
              headerLeading={
                <>
                  {nami.canDock && (
                    <AskAiButton
                      pressed={nami.panelOpen}
                      onToggle={nami.toggle}
                    />
                  )}
                  {headerLeading}
                </>
              }
              headerTrailing={
                <SheetClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={gt('Close', {
                      context: 'invoice slide-in: close the detail panel',
                    })}
                  >
                    <XIcon className="size-4" />
                  </Button>
                </SheetClose>
              }
            />
          ) : (
            <>
              <SheetHeader className="shrink-0 grow-0">
                <SheetTitle>
                  <T context="title for the invoice detail slide-in panel">
                    Invoice Details
                  </T>
                </SheetTitle>
              </SheetHeader>
              <div className="p-4 text-sm text-muted-foreground">
                <T context="placeholder text shown when no invoice is selected in the invoice detail panel">
                  No invoice selected
                </T>
              </div>
            </>
          )}
          <SheetFooter className="shrink-0 grow-0 flex-row items-center justify-between gap-2 border-t">
            <Button
              onClick={() => {
                onOpenChange(false);
              }}
              className="w-fit"
            >
              <T context="button label to close the invoice detail panel">
                Close
              </T>
            </Button>
            <InvoiceSlideinActionsSlot className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-2" />
          </SheetFooter>
        </InvoiceSlideinActionsProvider>
      </SheetContentResizable>
    </Sheet>
  );
}
